import type { NodeMetrics, EdgeMetrics } from '@/domain/metrics/contracts';
import { NodeMetricsSchema, EdgeMetricsSchema } from '@/domain/metrics/contracts';
import {
  SimulationGraphExtensionSchema,
  emptySimulationExtension,
  type SimulationGraphExtension,
  type SimulationGraphInput,
} from '@/domain/simulation/contracts';
import { getNodeDefinition } from '../nodes/registry/node-registry';
import type { EditorGraph } from '../domain/editor-graph';

function readNodeMetrics(data: Record<string, unknown>): NodeMetrics {
  const parsed = NodeMetricsSchema.safeParse(data.metrics);
  return parsed.success ? parsed.data : {};
}

function readEdgeMetrics(data: Record<string, unknown> | undefined): EdgeMetrics {
  const parsed = EdgeMetricsSchema.safeParse(data?.metrics);
  return parsed.success ? parsed.data : {};
}

/**
 * SPEC-009 plan §36 — the bridge between the React-facing EditorGraph/Node
 * Registry and the framework-free `src/domain/simulation` engine, mirroring
 * SPEC-008's `evaluate-graph-metrics.ts`. This is the only place that reads
 * Node Registry roles for simulation purposes.
 */
export function toSimulationGraphInput(graph: EditorGraph): SimulationGraphInput {
  return {
    nodes: graph.nodes.map((node) => {
      const definition = getNodeDefinition(node.type ?? '');
      const metrics = readNodeMetrics(node.data as Record<string, unknown>);
      const roles = [...new Set([...(definition?.metricRoles ?? []), ...(metrics.roleOverrides ?? [])])];
      return {
        id: node.id,
        metrics,
        roles,
        includeInFunnelInvestment: metrics.includeInFunnelInvestment,
      };
    }),
    edges: graph.edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      metrics: readEdgeMetrics(edge.data as Record<string, unknown> | undefined),
    })),
  };
}

/**
 * SPEC-009 §16/graph-schema.md §3 — reads the registered `simulation`
 * extension from a persisted/loaded graph, defaulting to empty rather than
 * inventing scenario data when absent or malformed (Zod boundary, rule §27).
 */
export function readSimulationExtension(graph: EditorGraph): SimulationGraphExtension {
  const parsed = SimulationGraphExtensionSchema.safeParse((graph as { simulation?: unknown }).simulation);
  return parsed.success ? parsed.data : emptySimulationExtension();
}
